import { HTTP_METHODS } from '../constants/httpMethods';
import { apiService, apiUploadService } from './ApiService';
import { LoginResponse } from '../interfaces/responses/ILoginResponse';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { IRegisterResponse } from '../interfaces/responses/IRegisterResponse';
import { ApiResponse } from '../interfaces/responses/ApiResponse';
import { UserProfileResponse, AvatarUploadResponse } from '../interfaces/responses/IUserProfileResponse';
import { Enable2FAResponse } from '../interfaces/responses/IEnable2FAResponse';
import { Verify2FASetupResponse } from '../interfaces/responses/IVerify2FASetupResponse';
import { Disable2FAResponse } from '../interfaces/responses/IDisable2FAResponse';
import { Login2FAResponse } from '../interfaces/responses/ILogin2FAResponse';
import { VerifyBackupCodeResponse } from '../interfaces/responses/IVerifyBackupCodeResponse';


// Every endpoint in this file now lives on HeavyDutyMachineryApi's own
// self-contained AuthController/AgeVerificationController/AccountMediaController
// (see that repo's Program.cs) instead of the shared, multi-tenant
// AuthorizationApi -- kept the name "baseUrl" to avoid touching every call
// site below for a purely cosmetic rename.
const baseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

export const loginUser = async (
  email: string,
  password: string
): Promise<LoginResponse> => {
  const response = await apiService<ApiResponse<LoginResponse>>({
    url: API_ENDPOINTS.LOGIN,
    method: HTTP_METHODS.POST,
    body: { email, password },
    baseUrl: baseUrl
  });

  if (response.success) {
    return response.data.data;
  } else {
    throw new Error(response.error || 'Login failed');
  }
};

export const registerUser = async (
  email: string,
  password: string,
): Promise<IRegisterResponse> => {
  const response = await apiService<ApiResponse<IRegisterResponse>>({
    url: API_ENDPOINTS.REGISTER,
    method: HTTP_METHODS.POST,
    body: { email, password },
    baseUrl: baseUrl
  });

  if (response.success) {
    return response.data.data;
  } else {
    throw new Error(response.error || 'Registration failed');
  }
};

export const checkEmailExists = async (email: string): Promise<boolean> => {
  const response = await apiService<ApiResponse<boolean>>({
    url: API_ENDPOINTS.CHECK_EMAIL_EXISTS,
    method: HTTP_METHODS.POST,
    body: { email },
    baseUrl: baseUrl
  });

  if (response.success) {
    return response.data.data;
  } else {
    throw new Error(response.error || 'Email check failed');
  }
};

export const confirmAccount = async (
  token: string,
  userId: string,
): Promise<void> => {
  const response = await apiService<ApiResponse<void>>({
    url: API_ENDPOINTS.CONFIRM_ACCOUNT,
    method: HTTP_METHODS.GET,
    params: { userId, token },
    baseUrl: baseUrl
  });

  if (!response.success) {
    throw new Error(response.error || 'Account confirmation failed');
  }
};

export const resendConfirmation = async (email: string): Promise<void> => {
  const response = await apiService<ApiResponse<boolean>>({
    url: API_ENDPOINTS.RESEND_CONFIRMATION,
    method: HTTP_METHODS.POST,
    body: { email },
    baseUrl: baseUrl
  });

  if (!response.success) {
    throw new Error(response.error || 'Failed to resend confirmation email');
  }
};

export const resetPassword = async (email: string): Promise<void> => {
  const response = await apiService<ApiResponse<void>>({
    url: API_ENDPOINTS.RESET_PASSWORD,
    method: HTTP_METHODS.POST,
    body: { email },
    baseUrl: baseUrl
  });

  if (!response.success) {
    throw new Error(response.error || 'Password reset request failed');
  }
};

export const updatePassword = async (
  newPassword: string,
  token: string | null,
  email: string | null
): Promise<void> => {
  const response = await apiService<ApiResponse<void>>({
    url: API_ENDPOINTS.UPDATE_PASSWORD,
    method: HTTP_METHODS.POST,
    body: { token, email, newPassword },
    baseUrl: baseUrl
  });

  if (!response.success) {
    throw new Error(response.error || 'Password update failed');
  }
};

// ✅ NUEVO: Change Password
export const changePassword = async (
  currentPassword: string,
  newPassword: string,
  confirmNewPassword: string
): Promise<void> => {
  const response = await apiService<ApiResponse<string>>({
    url: API_ENDPOINTS.CHANGE_PASSWORD,
    method: HTTP_METHODS.POST,
    body: { currentPassword, newPassword, confirmNewPassword },
    baseUrl: baseUrl
  });

  if (!response.success) {
    throw new Error(response.error || 'Password change failed');
  }
};

// ✅ NUEVO: Request Account Deletion
export const requestAccountDeletion = async (
  password: string,
  reason?: string
): Promise<{
  deletionDate: string;
  applicationName: string;
  message: string;
}> => {
  const response = await apiService<ApiResponse<{
    deletionDate: string;
    applicationName: string;
    message: string;
  }>>({
    url: API_ENDPOINTS.REQUEST_ACCOUNT_DELETION,
    method: HTTP_METHODS.POST,
    body: { password, reason },
    baseUrl: baseUrl
  });

  if (response.success) {
    return response.data.data;
  } else {
    throw new Error(response.error || 'Account deletion request failed');
  }
};

// ✅ NUEVO: Cancel Account Deletion (Public endpoint)
export const cancelAccountDeletion = async (
  token: string,
  userId: string,
  applicationId: string
): Promise<void> => {
  const response = await apiService<ApiResponse<string>>({
    url: API_ENDPOINTS.CANCEL_ACCOUNT_DELETION,
    method: HTTP_METHODS.POST,
    body: { token, userId, applicationId },
    baseUrl: baseUrl
  });

  if (!response.success) {
    throw new Error(response.error || 'Failed to cancel account deletion');
  }
};

// =============================================================================
// ENABLE 2FA - Initiate 2FA setup
// =============================================================================
export const enable2FA = async (): Promise<Enable2FAResponse> => {
  const response = await apiService<ApiResponse<Enable2FAResponse>>({
    url: API_ENDPOINTS.ENABLE_2FA,
    method: HTTP_METHODS.POST,
    baseUrl: baseUrl
  });

  if (response.success) {
    return response.data.data;  // ✅ .data.data
  } else {
    throw new Error(response.error || 'Failed to enable 2FA');
  }
};

// =============================================================================
// VERIFY 2FA SETUP - Complete 2FA activation
// =============================================================================
export const verify2FASetup = async (code: string): Promise<Verify2FASetupResponse> => {
  const response = await apiService<ApiResponse<Verify2FASetupResponse>>({
    url: API_ENDPOINTS.VERIFY_2FA_SETUP,
    method: HTTP_METHODS.POST,
    body: { code },
    baseUrl: baseUrl
  });

  if (response.success) {
    return response.data.data;  // ✅ .data.data
  } else {
    throw new Error(response.error || 'Failed to verify 2FA setup');
  }
};

// =============================================================================
// DISABLE 2FA - Turn off two-factor authentication
// =============================================================================
export const disable2FA = async (password: string): Promise<Disable2FAResponse> => {
  const response = await apiService<ApiResponse<Disable2FAResponse>>({
    url: API_ENDPOINTS.DISABLE_2FA,
    method: HTTP_METHODS.POST,
    body: { password },
    baseUrl: baseUrl
  });

  if (response.success) {
    return response.data.data;  // ✅ .data.data
  } else {
    throw new Error(response.error || 'Failed to disable 2FA');
  }
};

// =============================================================================
// LOGIN WITH 2FA - Complete login flow
// =============================================================================
export const login2FA = async (userId: string, code: string): Promise<Login2FAResponse> => {
  const response = await apiService<ApiResponse<Login2FAResponse>>({
    url: API_ENDPOINTS.LOGIN_2FA,
    method: HTTP_METHODS.POST,
    body: { userId, code },
    baseUrl: baseUrl
  });

  if (response.success) {
    return response.data.data;  // ✅ .data.data
  } else {
    throw new Error(response.error || 'Invalid authentication code');
  }
};

// =============================================================================
// VERIFY BACKUP CODE - Login with backup code
// =============================================================================
export const verifyBackupCode = async (
  userId: string, 
  backupCode: string
): Promise<VerifyBackupCodeResponse> => {
  const response = await apiService<ApiResponse<VerifyBackupCodeResponse>>({
    url: API_ENDPOINTS.VERIFY_BACKUP_CODE,
    method: HTTP_METHODS.POST,
    body: { userId, backupCode },
    baseUrl: baseUrl
  });

  if (response.success) {
    return response.data.data;  // ✅ .data.data
  } else {
    throw new Error(response.error || 'Invalid backup code');
  }
};

/**
 * Obtiene el estado actual de 2FA del usuario
 */
export const get2FAStatus = async (): Promise<boolean> => {
  const response = await apiService<ApiResponse<{ twoFactorEnabled: boolean }>>({
    url: API_ENDPOINTS.GET_2FA_STATUS,
    method: HTTP_METHODS.GET,
    baseUrl: baseUrl
  });

  if (response.success) {
    return response.data.data.twoFactorEnabled;
  } else {
    throw new Error(response.error || 'Failed to get 2FA status');
  }
};

/**
 * Obtiene el nombre completo y la foto de perfil guardados en el backend.
 */
export const getProfile = async (): Promise<UserProfileResponse> => {
  const response = await apiService<ApiResponse<UserProfileResponse>>({
    url: API_ENDPOINTS.PROFILE,
    method: HTTP_METHODS.GET,
    baseUrl: baseUrl
  });

  if (response.success) {
    return response.data.data;
  } else {
    throw new Error(response.error || 'Failed to load profile');
  }
};

/**
 * Persiste el nombre completo, la foto de perfil y/o el idioma de interfaz
 * editados en /profile (Mi cuenta) -- PreferredLanguage vive en el propio
 * User (AuthorizationApi), no solo en la cache local de i18next-detector,
 * para que siga a la cuenta entre dispositivos.
 */
export const updateProfile = async (fields: { fullName?: string; photoUrl?: string; preferredLanguage?: string }): Promise<UserProfileResponse> => {
  const response = await apiService<ApiResponse<UserProfileResponse>>({
    url: API_ENDPOINTS.PROFILE,
    method: HTTP_METHODS.PUT,
    body: fields,
    baseUrl: baseUrl
  });

  if (response.success) {
    return response.data.data;
  } else {
    throw new Error(response.error || 'Failed to update profile');
  }
};

/**
 * Sube la foto de perfil a Blob Storage y devuelve su URL pública — el
 * caller todavía tiene que llamar a updateProfile() para persistirla.
 */
// =============================================================================
// ACTIVE SESSIONS -- "Configuración > Acceso y seguridad > Dispositivos"
// =============================================================================
export interface IActiveSession {
  id: number;
  deviceInfo: string;
  ipAddress: string;
  location: string;
  createdAt: string;
  lastUsedAt?: string;
  expiresAt: string;
  isCurrent: boolean;
}

export const getActiveSessions = async (): Promise<IActiveSession[]> => {
  const response = await apiService<ApiResponse<{ sessions: IActiveSession[]; totalActiveSessions: number }>>({
    url: API_ENDPOINTS.ACTIVE_SESSIONS,
    method: HTTP_METHODS.GET,
    baseUrl: baseUrl,
  });
  if (response.success) return response.data.data.sessions;
  throw new Error(response.error || 'Failed to load active sessions');
};

export const revokeSession = async (sessionId: number): Promise<void> => {
  const response = await apiService<ApiResponse<string>>({
    url: API_ENDPOINTS.REVOKE_SESSION(sessionId),
    method: HTTP_METHODS.DELETE,
    baseUrl: baseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to revoke session');
};

export const revokeAllOtherSessions = async (): Promise<void> => {
  const response = await apiService<ApiResponse<string>>({
    url: API_ENDPOINTS.REVOKE_ALL_OTHER_SESSIONS,
    method: HTTP_METHODS.POST,
    baseUrl: baseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to revoke other sessions');
};

// =============================================================================
// LINKED OAUTH PROVIDERS -- "Configuración > Acceso y seguridad > Google"
// =============================================================================
export interface ILinkedProvider {
  provider: string;
  providerKey: string;
  linkedAt: string;
}

export const getLinkedOAuthProviders = async (): Promise<ILinkedProvider[]> => {
  const response = await apiService<ApiResponse<ILinkedProvider[]>>({
    url: API_ENDPOINTS.OAUTH_PROVIDERS,
    method: HTTP_METHODS.GET,
    baseUrl: baseUrl,
  });
  if (response.success) return response.data.data;
  throw new Error(response.error || 'Failed to load linked providers');
};

export const unlinkOAuthProvider = async (provider: string): Promise<void> => {
  const response = await apiService<ApiResponse<string>>({
    url: API_ENDPOINTS.OAUTH_UNLINK(provider),
    method: HTTP_METHODS.DELETE,
    baseUrl: baseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to unlink provider');
};

export const uploadAvatar = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiUploadService<ApiResponse<AvatarUploadResponse>>({
    url: API_ENDPOINTS.MEDIA_AVATAR_UPLOAD,
    formData,
    baseUrl: baseUrl
  });

  if (response.success) {
    return response.data.data.url;
  } else {
    throw new Error(response.error || 'Failed to upload photo');
  }
};

